import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Save, X, MapPin, Mail, Phone, Globe, Instagram, Facebook, Loader2, Edit } from 'lucide-react';
import { BadgeAssignment } from './BadgeAssignment';
import { useBusinessBadges } from '@/hooks/useBadgeManagement';
import { PhoneInput } from '@/components/ui/phone-input';
import { getBusinessStatus } from '@/utils/businessUtils';
import BusinessSubscriptionBadge from './BusinessSubscriptionBadge';
import BusinessSheetHeader from './BusinessSheetHeader';
import { useCategories } from '@/hooks/useCategories';

interface BusinessEditSheetProps {
  businessId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

interface BusinessData {
  id: string;
  name: string;
  contact_email: string;
  contact_phone: string;
  website: string;
  instagram_url: string;
  facebook_url: string;
  tiktok_url: string;
  address_line1: string;
  address_line2: string;
  city: string;
  county: string;
  postal_code: string;
  country: string;
  category_id: string;
  description: string;
  is_active: boolean;
  is_listed: boolean;
  company_banner_url?: string | null;
  company_logo?: string | null;
}

export const BusinessEditSheet: React.FC<BusinessEditSheetProps> = ({
  businessId,
  isOpen,
  onClose
}) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingSection, setEditingSection] = useState<'details' | 'contact' | 'location' | null>(null);
  const { data: categoriesData } = useCategories();
  
  // State for image uploads
  const [uploadingImages, setUploadingImages] = useState({
    banner: false,
    logo: false
  });
  
  // Form data state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category_id: '',
    contact_email: '',
    contact_phone: '',
    website: '',
    instagram_url: '',
    facebook_url: '',
    tiktok_url: '',
    address_line1: '',
    address_line2: '',
    city: '',
    county: '',
    postal_code: '',
    country: ''
  });

  // Fetch business data
  const { data: business, isLoading, error } = useQuery({
    queryKey: ['admin-business', businessId],
    queryFn: async () => {
      if (!businessId) return null;
      
      const { data, error } = await supabase
        .from('businesses')
        .select(`
          id, 
          name, 
          description,
          contact_email,
          contact_phone,
          website,
          instagram_url,
          facebook_url,
          tiktok_url,
          address_line1,
          address_line2,
          city,
          county,
          postal_code,
          country,
          category_id,
          is_active,
          is_listed,
          company_banner_url,
          company_logo
        `)
        .eq('id', businessId)
        .single();

      if (error) throw error;
      return data as BusinessData;
    },
    enabled: !!businessId && isOpen,
  });

  // Fetch business badges
  const { data: businessBadges = [] } = useBusinessBadges(businessId || '');

  // Initialize form data when business data loads
  useEffect(() => {
    if (business) {
      setFormData({
        name: business.name || '',
        description: business.description || '',
        category_id: business.category_id || '',
        contact_email: business.contact_email || '',
        contact_phone: business.contact_phone || '',
        website: business.website || '',
        instagram_url: business.instagram_url || '',
        facebook_url: business.facebook_url || '',
        tiktok_url: business.tiktok_url || '',
        address_line1: business.address_line1 || '',
        address_line2: business.address_line2 || '',
        city: business.city || '',
        county: business.county || '',
        postal_code: business.postal_code || '',
        country: business.country || ''
      });
    }
  }, [business]);

  // Update business mutation
  const updateBusinessMutation = useMutation({
    mutationFn: async (updateData: Partial<typeof formData>) => {
      if (!businessId) throw new Error('No business ID');
      
      const { error } = await supabase
        .from('businesses')
        .update(updateData)
        .eq('id', businessId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-business', businessId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'businesses'] });
      toast({
        title: "Success",
        description: "Business information updated successfully",
      });
      setEditingSection(null);
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to update business information",
      });
    },
  });

  // Update business images mutation
  const updateImagesMutation = useMutation({
    mutationFn: async (imageData: {
      company_logo?: string | null;
      company_banner_url?: string | null;
    }) => {
      if (!businessId) throw new Error('No business ID');
      
      const { error } = await supabase
        .from('businesses')
        .update(imageData)
        .eq('id', businessId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-business', businessId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'businesses'] });
      toast({
        title: "Success",
        description: "Business images updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to update business images",
      });
    },
  });

  // Handle image upload from file
  const handleImageUploadFromFile = async (file: File, imageType: 'banner' | 'logo') => {
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Please upload an image smaller than 5MB',
        variant: 'destructive'
      });
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload an image file (PNG, JPG, JPEG, GIF)',
        variant: 'destructive'
      });
      return;
    }

    setUploadingImages(prev => ({ ...prev, [imageType]: true }));

    try {
      // Get user ID for folder structure
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');
      
      const userId = user.id;
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
      
      const folderMap = {
        banner: 'banner',
        logo: 'logo'
      };
      
      const filePath = `${userId}/${folderMap[imageType]}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('business_images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('business_images')
        .getPublicUrl(filePath);

      const fieldMap = {
        banner: 'company_banner_url',
        logo: 'company_logo'
      };

      updateImagesMutation.mutate({ [fieldMap[imageType]]: publicUrl });
      
      toast({
        title: 'Image uploaded',
        description: 'Your image was uploaded successfully',
      });
    } catch (error: any) {
      console.error('Error uploading file:', error);
      toast({
        title: 'Upload failed',
        description: error.message || 'There was a problem uploading your image',
        variant: 'destructive'
      });
    } finally {
      setUploadingImages(prev => ({ ...prev, [imageType]: false }));
    }
  };

  // Handle image removal
  const handleImageRemove = (imageType: 'banner' | 'logo') => {
    const fieldMap = {
      banner: 'company_banner_url',
      logo: 'company_logo'
    };

    updateImagesMutation.mutate({ [fieldMap[imageType]]: null });
  };

  // Get category name
  const getCategoryName = (categoryId: string): string => {
    if (!categoriesData?.categories) return 'Uncategorized';
    const category = categoriesData.categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Uncategorized';
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhoneChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      contact_phone: value
    }));
  };

  const handleSave = (section: 'details' | 'contact' | 'location') => {
    let updateData: Partial<typeof formData> = {};
    
    if (section === 'details') {
      updateData = {
        name: formData.name,
        description: formData.description,
        category_id: formData.category_id,
      };
    } else if (section === 'contact') {
      updateData = {
        contact_email: formData.contact_email,
        contact_phone: formData.contact_phone,
        website: formData.website,
        instagram_url: formData.instagram_url,
        facebook_url: formData.facebook_url,
        tiktok_url: formData.tiktok_url,
      };
    } else if (section === 'location') {
      updateData = {
        address_line1: formData.address_line1,
        address_line2: formData.address_line2,
        city: formData.city,
        county: formData.county,
        postal_code: formData.postal_code?.toUpperCase() || '',
        country: formData.country,
      };
    }
    
    updateBusinessMutation.mutate(updateData);
  };

  const handleCancel = (section: 'details' | 'contact' | 'location') => {
    // Reset form data to original values
    if (business) {
      if (section === 'details') {
        setFormData(prev => ({
          ...prev,
          name: business.name || '',
          description: business.description || '',
          category_id: business.category_id || '',
        }));
      } else if (section === 'contact') {
        setFormData(prev => ({
          ...prev,
          contact_email: business.contact_email || '',
          contact_phone: business.contact_phone || '',
          website: business.website || '',
          instagram_url: business.instagram_url || '',
          facebook_url: business.facebook_url || '',
          tiktok_url: business.tiktok_url || '',
        }));
      } else if (section === 'location') {
        setFormData(prev => ({
          ...prev,
          address_line1: business.address_line1 || '',
          address_line2: business.address_line2 || '',
          city: business.city || '',
          county: business.county || '',
          postal_code: business.postal_code || '',
          country: business.country || '',
        }));
      }
    }
    setEditingSection(null);
  };

  const renderDetailsSection = () => (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-lg">Business Details</CardTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setEditingSection(editingSection === 'details' ? null : 'details')}
          disabled={updateBusinessMutation.isPending}
        >
          <Edit className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {editingSection === 'details' ? (
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Business Name</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter business name"
              />
            </div>
            
            <div>
              <Label htmlFor="category">Category</Label>
              <Select 
                value={formData.category_id} 
                onValueChange={(value) => handleSelectChange('category_id', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categoriesData?.categories?.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Describe your business, services, and what makes you unique..."
                className="min-h-[100px]"
              />
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button onClick={() => handleSave('details')} disabled={updateBusinessMutation.isPending}>
                {updateBusinessMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                <Save className="h-4 w-4 mr-2" />
                Save
              </Button>
              <Button variant="outline" onClick={() => handleCancel('details')}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <p className="text-sm text-muted-foreground">Business Name</p>
              <p className="text-sm font-medium">{business?.name}</p>
            </div>
            
            <div>
              <p className="text-sm text-muted-foreground">Category</p>
              <p className="text-sm font-medium">{getCategoryName(business?.category_id || '')}</p>
            </div>
            
            <div>
              <p className="text-sm text-muted-foreground">Description</p>
              <p className="text-sm">{business?.description || 'No description provided'}</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderContactSection = () => (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-lg">Contact Information</CardTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setEditingSection(editingSection === 'contact' ? null : 'contact')}
          disabled={updateBusinessMutation.isPending}
        >
          <Edit className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {editingSection === 'contact' ? (
          <div className="space-y-4">
            <div>
              <Label htmlFor="contact_email">Email Address</Label>
              <Input
                id="contact_email"
                name="contact_email"
                type="email"
                value={formData.contact_email}
                onChange={handleInputChange}
                placeholder="business@example.com"
              />
            </div>
            
            <div>
              <Label htmlFor="contact_phone">Phone Number</Label>
              <PhoneInput
                value={formData.contact_phone}
                onChange={handlePhoneChange}
                placeholder="Enter phone number"
              />
            </div>
            
            <div>
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                name="website"
                type="url"
                value={formData.website}
                onChange={handleInputChange}
                placeholder="https://example.com"
              />
            </div>
            
            <div>
              <Label htmlFor="instagram_url">Instagram</Label>
              <Input
                id="instagram_url"
                name="instagram_url"
                type="url"
                value={formData.instagram_url}
                onChange={handleInputChange}
                placeholder="https://instagram.com/yourbusiness"
              />
            </div>
            
            <div>
              <Label htmlFor="facebook_url">Facebook</Label>
              <Input
                id="facebook_url"
                name="facebook_url"
                type="url"
                value={formData.facebook_url}
                onChange={handleInputChange}
                placeholder="https://facebook.com/yourbusiness"
              />
            </div>
            
            <div>
              <Label htmlFor="tiktok_url">TikTok</Label>
              <Input
                id="tiktok_url"
                name="tiktok_url"
                type="url"
                value={formData.tiktok_url}
                onChange={handleInputChange}
                placeholder="https://tiktok.com/@yourbusiness"
              />
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button onClick={() => handleSave('contact')} disabled={updateBusinessMutation.isPending}>
                {updateBusinessMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                <Save className="h-4 w-4 mr-2" />
                Save
              </Button>
              <Button variant="outline" onClick={() => handleCancel('contact')}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {business?.contact_email && (
              <div className="flex items-center text-sm min-w-0">
                <Mail className="h-4 w-4 mr-2 text-muted-foreground flex-shrink-0" />
                <span className="truncate" title={business.contact_email}>{business.contact_email}</span>
              </div>
            )}
            
            {business?.contact_phone && (
              <div className="flex items-center text-sm min-w-0">
                <Phone className="h-4 w-4 mr-2 text-muted-foreground flex-shrink-0" />
                <span className="truncate" title={business.contact_phone}>{business.contact_phone}</span>
              </div>
            )}
            
            {business?.website && (
              <a 
                href={business.website} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center text-sm min-w-0 hover:text-primary transition-colors"
              >
                <Globe className="h-4 w-4 mr-2 text-muted-foreground flex-shrink-0" />
                <span className="truncate text-xs" title={business.website}>{business.website}</span>
              </a>
            )}
            
            {business?.instagram_url && (
              <a 
                href={business.instagram_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center text-sm min-w-0 hover:text-primary transition-colors"
              >
                <Instagram className="h-4 w-4 mr-2 text-muted-foreground flex-shrink-0" />
                <span className="truncate text-xs" title={business.instagram_url}>{business.instagram_url}</span>
              </a>
            )}
            
            {business?.facebook_url && (
              <a 
                href={business.facebook_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center text-sm min-w-0 hover:text-primary transition-colors"
              >
                <Facebook className="h-4 w-4 mr-2 text-muted-foreground flex-shrink-0" />
                <span className="truncate text-xs" title={business.facebook_url}>{business.facebook_url}</span>
              </a>
            )}
            
            {business?.tiktok_url && (
              <a 
                href={business.tiktok_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center text-sm min-w-0 hover:text-primary transition-colors"
              >
                <svg
                  className="h-4 w-4 mr-2 text-muted-foreground flex-shrink-0"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 12a4 4 0 1 0 0 8 4 4 0 0 0 0-8z" />
                  <path d="M15 8a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" />
                  <path d="M15 2v20" />
                  <path d="M9 15v7" />
                  <path d="M15 13h-3a4 4 0 0 0-4 4" />
                </svg>
                <span className="truncate text-xs" title={business.tiktok_url}>{business.tiktok_url}</span>
              </a>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderLocationSection = () => (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-lg">Location Information</CardTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setEditingSection(editingSection === 'location' ? null : 'location')}
          disabled={updateBusinessMutation.isPending}
        >
          <Edit className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {editingSection === 'location' ? (
          <div className="space-y-4">
            <div>
              <Label htmlFor="address_line1">Address Line 1</Label>
              <Input
                id="address_line1"
                name="address_line1"
                value={formData.address_line1}
                onChange={handleInputChange}
                placeholder="123 Main Street"
              />
            </div>
            
            <div>
              <Label htmlFor="address_line2">Address Line 2 (Optional)</Label>
              <Input
                id="address_line2"
                name="address_line2"
                value={formData.address_line2}
                onChange={handleInputChange}
                placeholder="Suite 100"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="City"
                />
              </div>
              
              <div>
                <Label htmlFor="county">County</Label>
                <Input
                  id="county"
                  name="county"
                  value={formData.county}
                  onChange={handleInputChange}
                  placeholder="County"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="postal_code">Postal Code</Label>
                <Input
                  id="postal_code"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleInputChange}
                  placeholder="12345"
                />
              </div>
              
              <div>
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  placeholder="Country"
                />
              </div>
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button onClick={() => handleSave('location')} disabled={updateBusinessMutation.isPending}>
                {updateBusinessMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                <Save className="h-4 w-4 mr-2" />
                Save
              </Button>
              <Button variant="outline" onClick={() => handleCancel('location')}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex text-sm min-w-0">
              <MapPin className="h-4 w-4 mr-2 text-muted-foreground shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                {business?.address_line1 && <p className="truncate" title={business.address_line1}>{business.address_line1}</p>}
                {business?.address_line2 && <p className="truncate" title={business.address_line2}>{business.address_line2}</p>}
                {business?.city && (
                  <p className="truncate" title={`${business.city}${business.county ? `, ${business.county}` : ''}${business.postal_code ? ` ${business.postal_code}` : ''}`}>
                    {business.city}
                    {business.county && `, ${business.county}`}
                    {business.postal_code && ` ${business.postal_code}`}
                  </p>
                )}
                {business?.country && <p className="truncate" title={business.country}>{business.country}</p>}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (isLoading) {
    return (
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent className="sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>
              <Skeleton className="h-6 w-48" />
            </SheetTitle>
            <SheetDescription>
              <Skeleton className="h-4 w-64" />
            </SheetDescription>
          </SheetHeader>
          <div className="space-y-6 mt-6">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  if (error || !business) {
    return (
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent className="sm:max-w-2xl">
          <SheetHeader>
            <SheetTitle>Error</SheetTitle>
            <SheetDescription>
              Failed to load business information
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6">
            <p className="text-sm text-muted-foreground">
              {error instanceof Error ? error.message : 'An unknown error occurred'}
            </p>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-2xl sm:w-3/4 overflow-y-auto">
        <SheetHeader className="mb-6">
          <SheetTitle>Edit Business</SheetTitle>
          <SheetDescription>
            Update business description, contact information, location details, and manage badge assignments
          </SheetDescription>
        </SheetHeader>

        {/* Business Header with Banner and Logo */}
        <BusinessSheetHeader 
          business={business}
          categoryName={getCategoryName(business.category_id)}
          editMode={true}
          onBannerUpload={(file) => handleImageUploadFromFile(file, 'banner')}
          onLogoUpload={(file) => handleImageUploadFromFile(file, 'logo')}
          onBannerRemove={() => handleImageRemove('banner')}
          onLogoRemove={() => handleImageRemove('logo')}
          uploading={uploadingImages}
        />

        {/* Business Status */}
        <div className="px-6 pt-4 flex gap-2">
          {(() => {
            const status = getBusinessStatus(business.is_active, business.is_listed);
            return (
              <Badge variant="outline" className={status.className}>
                {status.label}
              </Badge>
            );
          })()}
          <BusinessSubscriptionBadge businessId={business.id} />
        </div>

        <div className="space-y-6 mt-6">
          {/* Business Details */}
          {renderDetailsSection()}
          
          <Separator />
          
          {/* Contact Information */}
          {renderContactSection()}
          
          <Separator />
          
          {/* Location Information */}
          {renderLocationSection()}
          
          <Separator />
          
          {/* Badge Assignment Section */}
          <div className="space-y-4">
            <BadgeAssignment
              businessId={business.id}
              businessName={business.name}
              assignedBadges={businessBadges}
              isListed={business.is_listed}
              onAssignmentChange={() => {
                queryClient.invalidateQueries({ queryKey: ['business-badges', business.id] });
              }}
            />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};